import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { UiButtonDirective } from '@template-ui/directives';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
  WorkflowDefinitionFamilySummary,
  WorkflowDefinitionFamilyVisibility,
} from './platform-workflow-definition-lifecycle.models';
import {
  WorkflowFamilyVisibilityRequest,
  WorkflowFamilyVisibilityState,
} from './workflow-family-visibility.component';

export interface WorkflowFamilyActionsState {
  row: WorkflowDefinitionFamilySummary;
  blocked: boolean;
  canAuthor: boolean;
  visibility: WorkflowDefinitionFamilyVisibility | null;
  visibilityAvailable: boolean;
  visibilityContext: string;
  visibilityState?: WorkflowFamilyVisibilityState;
}

export type WorkflowFamilyActionsParams =
  ICellRendererParams<WorkflowDefinitionFamilySummary> & {
    state: () => WorkflowFamilyActionsState | null;
    versions: () => void;
    drafts: (archived: boolean) => void;
    changeVisibility: (request: WorkflowFamilyVisibilityRequest) => void;
    refreshVisibility: () => void;
    retryVisibility: () => void;
  };

@Component({
  selector: 'phms-workflow-family-actions-cell',
  imports: [CommonModule, UiButtonDirective, MatMenuModule],
  host: { '(click)': '$event.stopPropagation()' },
  template: `
    <ng-container *ngIf="state as current">
      <button
        uiButton
        type="button"
        [matMenuTriggerFor]="actionsMenu"
        [disabled]="current.blocked"
        [attr.aria-label]="
          'Actions for ' + (current.row.displayName || current.row.workflowCode)
        "
      >
        Actions <span aria-hidden="true">▾</span>
      </button>
      <mat-menu #actionsMenu="matMenu" class="workflow-family-actions-menu" xPosition="before">
        <ng-container *ngIf="state as latest">
          <button
            *ngIf="latest.row.retainedVersionCount > 0"
            mat-menu-item
            type="button"
            [disabled]="latest.blocked"
            (click)="versions()"
          >
            View versions
          </button>
          <button
            mat-menu-item
            type="button"
            [disabled]="latest.blocked"
            (click)="drafts(false)"
          >
            Open drafts ({{ latest.row.openDraftCount }})
          </button>
          <button
            *ngIf="latest.row.archivedDraftCount > 0"
            mat-menu-item
            type="button"
            [disabled]="latest.blocked"
            (click)="drafts(true)"
          >
            Include archived drafts ({{ latest.row.archivedDraftCount }})
          </button>
          <button
            *ngIf="latest.canAuthor"
            mat-menu-item
            type="button"
            data-change-family-visibility
            [disabled]="!canChangeVisibility"
            (click)="toggleVisibility()"
          >
            {{
              latest.visibility?.isHidden === true
                ? 'Restore'
                : 'Hide for all authors'
            }}
          </button>
          <button
            *ngIf="latest.visibilityState?.retry"
            mat-menu-item
            type="button"
            [disabled]="
              latest.blocked ||
              latest.visibilityState?.busy ||
              !latest.canAuthor ||
              !latest.visibilityAvailable
            "
            (click)="retryVisibility()"
          >
            Retry visibility change
          </button>
          <button
            mat-menu-item
            type="button"
            [disabled]="latest.visibilityState?.busy === true"
            (click)="refreshVisibility()"
          >
            Refresh visibility
          </button>
          <p
            *ngIf="latest.visibilityState?.message"
            class="family-actions-status"
            role="status"
          >
            {{ latest.visibilityState?.message }}
          </p>
          <p
            *ngIf="!latest.visibilityAvailable"
            class="family-actions-status"
            role="status"
          >
            Shared visibility is unavailable on this installation.
          </p>
        </ng-container>
      </mat-menu>
    </ng-container>
  `,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        height: 100%;
      }
      button[uiButton] {
        gap: 0.4rem;
        min-height: 26px;
      }
      ::ng-deep .cdk-overlay-container:has(.workflow-family-actions-menu) {
        z-index: 2200;
      }
      /* Material renders the menu in the shared overlay container. */
      ::ng-deep .workflow-family-actions-menu.mat-mdc-menu-panel {
        --mat-menu-item-label-text-color: var(--custom-text-strong-color);
        --mat-menu-item-hover-state-layer-color: var(--custom-surface-muted-color);
        background: var(--custom-surface-elevated-color, Canvas);
        color: var(--custom-text-strong-color, CanvasText);
        border: 1px solid var(--custom-border-color);
        box-shadow: var(--custom-shadow-elevation-2);
      }
      .family-actions-status {
        max-width: 17rem;
        margin: 0.5rem 1rem;
        white-space: normal;
        font-size: 0.875rem;
      }
    `,
  ],
})
export class WorkflowFamilyActionsCellComponent implements ICellRendererAngularComp {
  private params?: WorkflowFamilyActionsParams;
  get state(): WorkflowFamilyActionsState | null {
    return this.params?.state() ?? null;
  }
  agInit(params: WorkflowFamilyActionsParams): void {
    this.params = params;
  }
  refresh(params: WorkflowFamilyActionsParams): boolean {
    this.agInit(params);
    return true;
  }
  versions(): void {
    if (this.state && !this.state.blocked) this.params?.versions();
  }
  drafts(archived: boolean): void {
    if (this.state && !this.state.blocked) this.params?.drafts(archived);
  }
  get canChangeVisibility(): boolean {
    const current = this.state,
      detail = current?.visibility;
    return !!(
      current?.canAuthor &&
      !current.blocked &&
      !current.visibilityState?.busy &&
      !current.visibilityState?.blocked &&
      current.visibilityAvailable &&
      detail?.isAvailable &&
      typeof detail.isHidden === 'boolean' &&
      Number.isInteger(detail.revision) &&
      detail.eTag
    );
  }
  toggleVisibility(): void {
    const current = this.state;
    if (current?.visibility && this.canChangeVisibility)
      this.params?.changeVisibility({
        detail: current.visibility,
        isHidden: !current.visibility.isHidden,
        context: current.visibilityContext,
      });
  }
  refreshVisibility(): void {
    if (this.state) this.params?.refreshVisibility();
  }
  retryVisibility(): void {
    if (this.state) this.params?.retryVisibility();
  }
}
